import {Component, OnInit} from '@angular/core';
import {Organization} from "app/models/organization.model";
import {Select} from "@ngxs/store";
import {OrganizationsState} from "app/state-management/states/organizations.states";
import {firstValueFrom, Observable} from "rxjs";
import {ActivatedRoute} from "@angular/router";
import {MatDialog} from "@angular/material/dialog";
import {UntilDestroy} from "@ngneat/until-destroy";
import {PromoApiService} from "app/services/promo-api.service";
import {DisplayedProduct, Group, Product, Promo, PromoDish} from "app/models/menu.model"
import {ProductApiService} from "app/services/product-api.service"
import {DishesService} from "app/services/dishes.service";
import {CdkDragDrop, moveItemInArray} from "@angular/cdk/drag-drop";
import {
  SelectProductsModalComponent,
  SelectProductsModalComponentDataType
} from "app/pages/menu/shared/select-products-modal/select-products-modal.component";
import {TranslateService} from "@ngx-translate/core";

@UntilDestroy()
@Component({
  selector: 'app-promo',
  templateUrl: './promo.component.html',
  styleUrls: ['./promo.component.scss']
})
export class PromoComponent implements OnInit {
  organization: Organization;
  @Select(OrganizationsState.getSelectedOrganization) selectedOrganization$: Observable<Organization>;
  loading: boolean = false;
  promoData: Promo = {dishes: []};
  dictionaryProducts: Map<string, DisplayedProduct> = new Map<string, DisplayedProduct>();
  dishes: string[] = [];
  menuId: string = '';
  products: DisplayedProduct[] = [];
  groups: Group[] = [];

  private readonly noop: (event?: any) => void = () => {};

  constructor(
    public activatedRoute: ActivatedRoute,
    public dialog: MatDialog,
    public promoService: PromoApiService,
    private productApiService: ProductApiService,
    private dishesService: DishesService,
    private translate: TranslateService
  ) {
  }

  async ngOnInit() {
    this.organization = await firstValueFrom(this.selectedOrganization$);
    this.menuId = await this.dishesService.getMenuIdByOrganization(this.organization.id);
    this.loading = true;
    try {
      await Promise.all([
        this.loadPromo(),
        this.loadProducts(),
        this.loadGroups()
      ]);
      // saved promo may reference dishes that were removed from the menu
      this.dishes = this.dishes.filter(id => this.dictionaryProducts.has(id));
      this.draw();
      this.loading = false;
    } catch (error) {
      console.error('Error loading promo data:', error);
      this.loading = false;
    }
  }

  async loadPromo() {
    const promo = await this.promoService.get(this.menuId);
    this.promoData = promo;
    this.dishes = [...promo.dishes].sort((a, b) => a.order - b.order).map(dish => dish.id);
  }

  async loadProducts() {
    const products = await this.productApiService.getProducts(this.menuId);
    products.forEach((product) => {
      this.dictionaryProducts.set(product.externalId, product);
    });
  }

  doNothing($event: any) {
  }

  dropProduct($event: CdkDragDrop<Product>) {
    if ($event.previousIndex !== $event.currentIndex) {
      moveItemInArray(this.dishes, $event.previousIndex, $event.currentIndex);
      this.draw();
    }
  }

  async submit() {
    this.loading = true;
    const updatedDishes: PromoDish[] = [];
    let i = 0;
    for (const dish of this.dishes) {
      updatedDishes.push({id: dish, order: i} as PromoDish)
      i++;
    }

    await this.promoService.createOrUpdate(this.menuId, updatedDishes);

    this.loading = false;
  }

  editDishesList() {
    this.dialog.open(SelectProductsModalComponent, {
      data: {
        title: this.translate.instant('MENU.PROMO.SELECT_DISHES'),
        productIds: this.dishes,
        products: this.dictionaryProducts,
        groups: this.groups,
        organization: this.organization,
      } as SelectProductsModalComponentDataType
    }).afterClosed().subscribe(async res => {
      if (res) {
        const newProductIds = res.data;
        this.dishes = [...newProductIds];
        this.draw();
      }
    });
  }

  draw() {
    this.products = [];
    for (let dish of this.dishes) {
      const product = this.dictionaryProducts.get(dish);
      if (product) {
        this.products.push(product);
      }
    }
  }

  private loadGroups() {
    if (this.groups && this.groups.length > 0) {
      return;
    }
    let response = this.dishesService.getGroupIncludeAllList(this.menuId);
    firstValueFrom(response).then(r => {
      this.groups = r.data;
    })
  }
}
